import { useLocation } from 'react-router-dom';
import { ArrowRightIcon, SparklesIcon, ShoppingBagIcon, MessageCircleIcon } from 'lucide-react';
import { useCart, useOverlayActions } from '../../contexts/StoreContext';
import { useScrolledPast } from '../../hooks/useScrolledPast';
import { founder, salon } from '../../data/content';
import { shopLabels } from '../../data/shopLabels';
import { euro } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { btnPriceBar } from '../../ui/styles';
import { FloatingBar, FloatingBarSpacer } from './FloatingBar';
const PRIMARY = 'flex min-h-[48px] min-w-0 flex-[1.35] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-ink px-4 text-[14px] text-shell transition-[background-color,transform] duration-150 ease-soft active:scale-[0.98] active:bg-clay';
const SECONDARY = 'flex min-h-[48px] min-w-0 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 text-[14px] text-ink transition-[background-color,transform] duration-150 ease-soft active:scale-[0.98] active:bg-sand';

/** Zichtbaar zodra je voorbij 60% van het eerste scherm bent: daarboven staan de knoppen van de hero zelf. */
const SHOW_AFTER = 0.6;
type StepProps = {
  pathname: string;
  openQuiz: () => void;
  openBooking: () => void;
};

/**
 * De volgende stap per pagina, zolang de mand leeg is. Altijd twee benoemde knoppen (geen raadsel-
 * icoontjes): salon = boeken + huidadvies, contact = WhatsApp + boeken, de rest = huidadvies + boeken.
 */
function NextStep({
  pathname,
  openQuiz,
  openBooking
}: StepProps) {
  const quiz = <>
      <SparklesIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7837" data-mp="3hsp5m9d1uoj5" />
      Huidadvies
    </>;
  if (pathname.startsWith('/behandelingen')) {
    return <>
        <button type="button" onClick={openBooking} aria-haspopup="dialog" className={PRIMARY} data-id="element-7838" data-mp="ebqgvlynybyc">
          Plan een behandeling
        </button>
        <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={SECONDARY} data-id="element-7839" data-mp="j2yqahzztxbi">
          {quiz}
        </button>
      </>;
  }
  if (pathname.startsWith('/contact')) {
    return <>
        <a href={salon.whatsapp} target="_blank" rel="noopener noreferrer" className={PRIMARY} data-id="element-7840" data-mp="nzvrbej0yx85">
          <MessageCircleIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7841" data-mp="iyeupv7kpwxd" />
          App {founder.firstName}
          <span className="sr-only" data-id="element-7842" data-mp="9zb7qg5acxdl">{shopLabels.viaWhatsApp}</span>
        </a>
        <button type="button" onClick={openBooking} aria-haspopup="dialog" className={SECONDARY} data-id="element-7843" data-mp="a0memou12l8x">
          Afspraak
        </button>
      </>;
  }
  return <>
      <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={PRIMARY} data-id="element-7844" data-mp="a9toviawq29">
        {quiz}
        <span className="text-shell/80" data-id="element-7845" data-mp="3rh49jzhnzz3e">· 1 min</span>
      </button>
      <button type="button" onClick={openBooking} aria-haspopup="dialog" className={SECONDARY} data-id="element-7846" data-mp="3nepr3ghwr1cn">
        Afspraak
      </button>
    </>;
}

/**
 * Zwevende actiebalk op telefoon en tablet. Met iets in de mand: de winkelmand, met de voortgang
 * naar het volgende cadeau als dunne lijn. Anders: de volgende stap van de pagina.
 * Verschijnt pas na het eerste scherm (behalve met iets in de mand), zodat hij de hero niet dubbelt.
 * Productpagina en Mijn omgeving hebben een eigen balk; afrekenen blijft afleidingsvrij.
 */
export function MobileActionBar() {
  const {
    openQuiz,
    openCart,
    openBooking
  } = useOverlayActions();
  const {
    count,
    subtotal,
    giftSubtotal,
    nextGift
  } = useCart();
  const {
    pathname
  } = useLocation();
  const scrolled = useScrolledPast(SHOW_AFTER);
  if (pathname === '/afrekenen' || pathname.startsWith('/bestelling/')) return null;
  if (pathname.startsWith('/webshop/') || pathname.startsWith('/account')) return <FloatingBarSpacer data-id="element-7847" data-mp="1szk6aw4ueusg" />;
  const giftLeft = nextGift ? Math.max(0, nextGift.threshold - giftSubtotal) : 0;
  const giftProgress = nextGift ? Math.min(1, giftSubtotal / nextGift.threshold) : 0;
  return <>
      <FloatingBarSpacer data-id="element-7848" data-mp="10fvd9l44n9dv" />
      <FloatingBar show={count > 0 || scrolled} data-id="element-7849" data-mp="334d9gqnzy4zm">
        {count > 0 ? <>
            {nextGift && <div className="px-4 pb-2 pt-1.5" data-id="element-7850" data-mp="2y5sgncb2qnl7">
                <p className="text-[12.5px] text-ink" data-id="element-7851" data-mp="2y73iv7ovfna7">
                  Nog <span className="tabular-nums" data-id="element-7852" data-mp="3bwxdy7qyz3sd">{euro(giftLeft)}</span> tot {nextGift.phrase}
                </p>
                <div aria-hidden="true" className="mt-1.5 h-0.5 w-full overflow-hidden rounded-full bg-line" data-id="element-7853" data-mp="3755hxhp1o5uu">
                  <div className="h-full origin-left rounded-full bg-sage transition-transform duration-300 ease-soft" style={{
              transform: `scaleX(${giftProgress})`
            }} data-id="element-7854" data-mp="3lta3quje8xhu" />
                </div>
              </div>}
            <button type="button" onClick={openCart} aria-haspopup="dialog" className={btnPriceBar} data-id="element-7855" data-mp="3utf78tlgvbqq">
              <span className="flex min-w-0 items-center gap-2" data-id="element-7856" data-mp="3qs3ieqaf2ujl">
                <ShoppingBagIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-7857" data-mp="20ia2flyn8t9t" />
                <span className="truncate" data-id="element-7858" data-mp="1whhpahmni489">
                  {plural(count, 'product', 'producten')} · <span className="tabular-nums" data-id="element-7859" data-mp="1md1dq0g7f9a9">{euro(subtotal)}</span>
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5" data-id="element-7860" data-mp="1qd21ibp70wtz">
                Bekijk
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7861" data-mp="1cldpe8ey28d0" />
              </span>
            </button>
          </> : <div className="flex gap-1" data-id="element-7862" data-mp="12o8y5u68rmpc">
            <NextStep pathname={pathname} openQuiz={openQuiz} openBooking={() => openBooking()} data-id="element-7863" data-mp="12n7qlzofpicd" />
          </div>}
      </FloatingBar>
    </>;
}